import { Alert, Box, Button, Stack, TextField, Typography } from "@mui/material"
import { If } from "lib/@components/switch"
import { Bound } from "lib/@components/binding/Bound"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { BoundStandardSwitch, BoundTextField } from "lib/@components/binding/bound-components"
import { useRefresh } from "lib/@hooks/useRefresh"
import { useSave } from "lib/@hooks/use-save"
import { Repeat } from "lib/@components/repeat"
import { getSSOConfig, getSSOServiceProviderDetails } from "routes/settings/plugins/sso/controller/get-sso-config"
import { saveSSOConfig, deleteSSOConfig } from "routes/settings/plugins/sso/controller/save-sso-config"
import { confirm } from "lib/@dialogs/confirm"
import { snackbar } from "lib/snackbar"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { useRef, useState } from "react"
import { validateSSODomain } from "routes/settings/plugins/sso/controller/validate-sso-domain"
import { DomainChip } from "./domain-chip"
import { SPMetadataSection } from "./sp-metadata-section"
import { RecentFailuresSection } from "./recent-failures-section"

export function SSOConfigPanel() {
    const { client } = useBoundContext()
    const clientId = client?.id
    const emptyConfig = useRef({})
    const config = getSSOConfig.useResultsOnce({ clientId }) ?? emptyConfig.current
    const spDetails = getSSOServiceProviderDetails.useResults({ clientId }) ?? {}
    const refresh = useRefresh()
    const [newDomain, setNewDomain] = useState("")
    const [domainError, setDomainError] = useState("")

    const onChange = useSave({ save: handleSave, target: config, wait: 2000 })
    const hasDomains = !!(config.emailDomains || []).length
    // Enforcement removes the passcode route for everyone on the domain, so the SSO route has to
    // work. Domains alone are not enough: with a blank IdP URL or certificate the tenant is locked
    // out entirely — `/auth/request` refuses a passcode and `/auth/sso/login` cannot build a
    // strategy. saveSSOConfig refuses the same combination; this keeps the panel from offering it.
    const canEnforce = hasDomains && !!config.ssoLoginUrl && !!config.idpCertificate
    // Never disable the way OUT. For a record already in the bad state the toggle renders checked,
    // and disabling it would leave the admin no targeted way to drop the enforcement claim — only
    // the master SSO switch, which turns the whole feature off. The server explicitly permits
    // clearing enforceSSO on such a record; the panel has to offer it.
    const enforceToggleDisabled = !canEnforce && !config.enforceSSO

    return (
        <Box sx={{ p: 3, maxWidth: 720 }}>
            <Typography variant="h6" gutterBottom>
                SSO Configuration
            </Typography>

            <Bound target={config} onChange={onChange} refresh={refresh}>
                <Stack spacing={3}>
                    <SPMetadataSection
                        entityId={spDetails.entityId}
                        acsUrl={spDetails.acsUrl}
                        metadataUrl={spDetails.metadataUrl}
                    >
                        <BoundTextField
                            field="spEntityId"
                            label="Custom Entity ID (optional)"
                            helperText="Override the auto-generated Entity ID shown above. Leave blank to use the default."
                        />
                    </SPMetadataSection>

                    <BoundTextField
                        field="providerName"
                        label="Identity Provider Display Name"
                        helperText="Shown on the SSO login button (e.g. 'Okta', 'Azure AD')"
                    />

                    <BoundTextField
                        field="ssoLoginUrl"
                        sideEffects={true}
                        data-cy="idp-login-url-field"
                        label="IdP SSO Login URL"
                        helperText="The SAML 2.0 Single Sign-On URL from your identity provider"
                    />

                    <BoundTextField
                        field="idpCertificate"
                        sideEffects={true}
                        label="IdP Certificate (X.509)"
                        multiline
                        rows={4}
                        data-cy="idp-certificate-field"
                        helperText="Public X.509 certificate from your IdP (PEM format)"
                    />

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            Email Domains
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Users with these email domains will be offered SSO login.
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, my: 1 }}>
                            <Repeat
                                list={config.emailDomains || []}
                                item={<DomainChip onRemove={handleRemoveDomain} />}
                                pass="domain"
                            />
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                            <TextField
                                size="small"
                                label="Add domain"
                                placeholder="example.com"
                                value={newDomain}
                                error={!!domainError}
                                helperText={domainError}
                                onChange={handleDomainChange}
                                onKeyDown={handleDomainKeyDown}
                                data-cy="add-domain-field"
                            />
                            <Button variant="outlined" size="small" onClick={handleAddDomain} sx={{ mt: "4px" }}>
                                Add
                            </Button>
                        </Stack>
                    </Box>

                    {/* SNAG-030: a config with no email domains matches no address, so SSO is
                        inert and enforcement resolves false — /auth/request keeps sending
                        passcodes. Say that plainly rather than letting the toggles imply
                        otherwise, and disable enforcement outright: saveSSOConfig refuses it
                        server-side, and the panel autosaves, so an enabled toggle here would
                        just produce an error snackbar the admin cannot act on. */}
                    <If value={!canEnforce && config.enabled}>
                        <Alert severity="warning" data-cy="sso-no-domains-warning">
                            {enforcementWarning()}
                        </Alert>
                    </If>

                    <BoundStandardSwitch
                        field="enforceSSO"
                        disabled={enforceToggleDisabled}
                        label="Enforce SSO (disable email login for these domains)"
                    />

                    <BoundStandardSwitch
                        field="wantAssertionsSigned"
                        label="Require signed SAML assertions"
                        defaultValue={true}
                    />

                    <Box>
                        <BoundStandardSwitch
                            field="wantAuthnResponseSigned"
                            label="Also require the whole SAML response to be signed"
                            defaultValue={false}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                            Extra protection on top of signed assertions: the entire response message from your identity
                            provider must be signed, not just the assertion inside it. Only turn this on if your IdP
                            signs the response — Okta does by default, whereas Microsoft Entra ID signs the assertion
                            only unless you change its signing option. If your IdP does not sign the response, every SSO
                            sign-in for these domains will fail. Leave this off if you are unsure.
                        </Typography>
                    </Box>

                    <Typography variant="subtitle2" sx={{ mt: 2 }}>
                        Advanced Attribute Mapping
                    </Typography>

                    <BoundTextField
                        field="emailAttribute"
                        label="Email Attribute"
                        helperText="SAML attribute containing the user's email address. When set, this takes precedence over the Name ID — use it when the IdP's Name ID is not the user's email (e.g. an Azure AD UPN). Leave blank to use the Name ID."
                    />

                    <BoundTextField
                        field="displayNameAttribute"
                        label="Display Name Attribute"
                        helperText="SAML attribute containing the user's display name. Leave blank for the default claim."
                    />

                    <RecentFailuresSection failures={config.recentFailures} />

                    <Box sx={{ pt: 2, display: "flex", gap: 2 }}>
                        <Button variant="contained" onClick={handleManualSave}>
                            Save Configuration
                        </Button>
                        <Button variant="outlined" color="error" onClick={handleDelete}>
                            Delete Configuration
                        </Button>
                    </Box>
                </Stack>
            </Bound>
        </Box>
    )

    function handleDomainKeyDown(e) {
        if (e.key === "Enter") {
            e.preventDefault()
            handleAddDomain()
        }
    }

    function handleDomainChange(e) {
        setNewDomain(e.target.value)
        if (domainError) setDomainError("")
    }

    async function handleAddDomain() {
        const domain = newDomain.trim().toLowerCase()
        if (!domain) return
        if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(domain)) {
            setDomainError("Please enter a valid domain (e.g. example.com)")
            return
        }
        const domains = config.emailDomains || []
        if (domains.includes(domain)) {
            setDomainError("This domain has already been added")
            return
        }
        try {
            const result = await validateSSODomain(domain, clientId)
            if (!result.valid) {
                setDomainError(result.error)
                errorSnackbar(result.error)
                return
            }
        } catch (e) {
            setDomainError("Unable to validate domain")
            errorSnackbar("Unable to validate domain")
            return
        }
        setDomainError("")
        config.emailDomains = [...domains, domain]
        setNewDomain("")
        onChange(config)
        refresh()
    }

    // Names the specific thing that is missing. "Something is wrong" is not actionable, and the
    // admin cannot see which of the three the server is objecting to.
    function enforcementWarning() {
        if (!hasDomains) {
            return `This configuration has no email domains, so it matches no one. SSO sign-in is not active${
                config.enabled ? " despite being enabled" : ""
            }, and SSO cannot be enforced. Add a domain above to activate it.`
        }
        if (!config.ssoLoginUrl) {
            return "Add the IdP SSO login URL before enforcing SSO. Enforcing removes passcode sign-in for these domains, so without a sign-in route nobody could get in at all."
        }
        return "Add the IdP certificate before enforcing SSO. Enforcing removes passcode sign-in for these domains, and without a certificate no sign-in can be validated."
    }

    function handleRemoveDomain(domain) {
        const remaining = (config.emailDomains || []).filter((d) => d !== domain)
        // SNAG-030: removing the LAST domain while SSO is enforced is refused server-side, because
        // enforcement with no domains claims password sign-in is off when it is not. Stop it here
        // too — otherwise the chip disappears, the save fails, and the panel is left displaying a
        // state the database does not hold. Same reasoning as disabling the enforce toggle: the UI
        // should not offer the action the server is going to reject.
        if (!remaining.length && config.enforceSSO) {
            errorSnackbar("Turn off Enforce SSO before removing the last email domain")
            return
        }
        config.emailDomains = remaining
        onChange(config)
        refresh()
    }

    // The server's refusals here name the thing to fix (a claimed domain, an absent domain while
    // enforcing). Reporting a bare "Failed to save SSO configuration" hides the one piece of
    // information the admin needs, so prefer the message and keep the generic text as the fallback
    // for a genuine transport failure.
    function saveFailureMessage(error) {
        const message = error?.message?.replace(/^GraphQL error:\s*/i, "").trim()
        return message || "Failed to save SSO configuration"
    }

    async function handleSave(data) {
        try {
            await saveSSOConfig({ ...data, clientId })
        } catch (e) {
            errorSnackbar(saveFailureMessage(e))
        }
    }

    async function handleManualSave() {
        try {
            await saveSSOConfig({ ...config, clientId })
            snackbar("SSO configuration saved")
        } catch (e) {
            errorSnackbar(saveFailureMessage(e))
        }
    }

    async function handleDelete() {
        const ok = await confirm(
            "Are you sure you want to delete the SSO configuration? Users will no longer be able to sign in via SSO.",
            "Delete SSO Configuration"
        )
        if (!ok) return
        try {
            await deleteSSOConfig({ clientId })
            snackbar("SSO configuration deleted")
            refresh()
        } catch (e) {
            errorSnackbar("Failed to delete SSO configuration")
        }
    }
}
